import { SelectItemProps } from '@ui/src';
import { COFAndIndividualQuestionnarieFormValues } from '../../../individual/types';
import {
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  ApplicationAgreement,
  ApplicationDocument,
  CRSAndFatcaFormValues,
  ExpectedFundsOriginsFormValues,
  FormValues,
  NewApplicationIndividualStepsStatuses,
  NewApplicationStepStatus,
  NewApplicationSumSubStatus,
} from '../../types';
import {
  OngoingMonitring,
  OngoingStatus,
  OngoingCheckingStatus,
  ApplicationScheme,
} from '../../../common/types';

export const getStepOneStatus = (
  values: Partial<FormValues>,
  stepsStatus?: NewApplicationStepStatus,
  sourcesOfFunds?: SelectItemProps[],
  clientsActivities?: SelectItemProps[],
  taxRequired?: boolean,
  applicationScheme?: ApplicationScheme,
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues,
) => {
  let stepOneStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const employmentClientActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );

  const otherClientActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );

  const selfEmployedActivity = clientsActivities?.find(
    (item) => item.name === 'Self-employed',
  );

  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const otherSoureOfFunds = sourcesOfFunds?.find(
    (item) => item.name === 'Other',
  );

  const taxCompleted = !taxRequired || (taxRequired && values.taxNumber);

  let generalInfoComplete = false;
  let activitiesComplete = true;
  let groupInformationComplete = true;
  let sourceOfIncomeComplete = true;
  let sourcesAndSizesOfWealthComplete = true;
  let netWorthComplete = true;

  if (applicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a') {
    groupInformationComplete = false;
    sourceOfIncomeComplete = false;
    sourcesAndSizesOfWealthComplete = false;
    netWorthComplete = false;

    if (
      values.firstName &&
      values.lastName &&
      values.phone &&
      values.dob &&
      values.placeOfBirth &&
      values.birthCountryId &&
      questionnarieFields?.maritalStatus
    ) {
      generalInfoComplete = true;
    }

    if (questionnarieFields?.maritalStatus === 'MARRIED') {
      if (!questionnarieFields?.spouseFullName) {
        generalInfoComplete = false;
      } else {
        generalInfoComplete = true;
      }
    }

    if (
      questionnarieFields?.belongsToGroup?.toString() !== 'null' &&
      questionnarieFields?.belongsToGroup?.toString() !== 'undefined'
    ) {
      if (questionnarieFields?.belongsToGroup === 'no') {
        groupInformationComplete = true;
      } else {
        groupInformationComplete = Boolean(
          questionnarieFields?.parentCompanyName &&
          questionnarieFields?.parentCompanyCountryId &&
          questionnarieFields?.subsidiariesInfo &&
          questionnarieFields?.mainActivities &&
          questionnarieFields?.groupWealthSourceIds?.length &&
          questionnarieFields?.estimatedTotalWealthEuro,
        );
      }
    }

    if (
      questionnarieFields?.requiresSourcesOfIncomes?.toString() !== 'null' &&
      questionnarieFields?.requiresSourcesOfIncomes?.toString() !== 'undefined'
    ) {
      if (questionnarieFields?.requiresSourcesOfIncomes === 'no') {
        sourceOfIncomeComplete = true;
      } else {
        sourceOfIncomeComplete = Boolean(
          questionnarieFields?.sourcesOfIncomes?.filter((item) => item.sourceId)
            ?.length,
        );
      }
    }

    if (
      questionnarieFields?.requiresSourceAndSizeOfWealth?.toString() !==
        'null' &&
      questionnarieFields?.requiresSourceAndSizeOfWealth?.toString() !==
        'undefined'
    ) {
      if (questionnarieFields?.requiresSourceAndSizeOfWealth === 'no') {
        sourcesAndSizesOfWealthComplete = true;
      } else {
        sourcesAndSizesOfWealthComplete = Boolean(
          questionnarieFields?.sourcesAndSizesOfWealth?.filter(
            (item) => item.sourceId,
          )?.length,
        );
      }
    }

    if (
      questionnarieFields?.requiresNetWorth?.toString() !== 'null' &&
      questionnarieFields?.requiresNetWorth?.toString() !== 'undefined'
    ) {
      if (questionnarieFields?.requiresNetWorth === 'no') {
        netWorthComplete = true;
      } else {
        netWorthComplete = Boolean(questionnarieFields?.netWorthAmount);
      }
    }
  } else {
    activitiesComplete = Boolean(
      values.sourceOfFundsIds &&
      values.sourceOfFundsIds.length &&
      values.clientActivityId &&
      taxCompleted &&
      values.deliveryChannelId,
    );

    if (values.firstName && values.lastName && values.phone && values.dob) {
      generalInfoComplete = true;
    }
  }

  const stepOne: Partial<FormValues> = {
    firstName: values.firstName,
    lastName: values.lastName,
    phone: values.phone,
    dob: values.dob,
    email: values.email,
    isUSResident: values.isUSResident,
    participatesInPolitics: values.participatesInPolitics,
    deliveryChannelId: values.deliveryChannelId,
    sourceOfFundsIds: values.sourceOfFundsIds,
    clientActivityId: values.clientActivityId,
  };

  if (values.taxNumber) {
    stepOne.taxNumber = values.taxNumber;
  }

  if (values.clientActivityId === otherClientActivity?.value) {
    activitiesComplete = Boolean(
      activitiesComplete && values.typeOfActivityDescription,
    );
    stepOne.typeOfActivityDescription = values.typeOfActivityDescription;
  }

  if (values.clientActivityId === employmentClientActivity?.value) {
    if (applicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a') {
      activitiesComplete = Boolean(
        activitiesComplete &&
        values.employeePosition &&
        values.nameOfEmployer &&
        values.countryOfEmployerId &&
        questionnarieFields?.natureOfBusinessActivity,
      );
    } else {
      activitiesComplete = Boolean(
        activitiesComplete &&
        values.employeePosition &&
        values.nameOfEmployer &&
        values.countryOfEmployerId,
      );
    }

    stepOne.employeePosition = values.employeePosition;
    stepOne.nameOfEmployer = values.nameOfEmployer;
    stepOne.countryOfEmployerId = values.countryOfEmployerId;
  }

  if (
    values.clientActivityId === selfEmployedActivity?.value &&
    applicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a'
  ) {
    activitiesComplete = Boolean(
      activitiesComplete && questionnarieFields?.natureOfBusinessActivity,
    );
  }

  if (
    values.clientActivityId === businessOwnerActivity?.value &&
    applicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a'
  ) {
    activitiesComplete = Boolean(
      activitiesComplete &&
      questionnarieFields?.natureOfBusinessActivity &&
      questionnarieFields?.nameOfCompany &&
      questionnarieFields?.ownership &&
      values?.countryOfEmployerId,
    );
  }

  let stepOneCompleted = Boolean(
    activitiesComplete &&
    generalInfoComplete &&
    groupInformationComplete &&
    sourceOfIncomeComplete &&
    sourcesAndSizesOfWealthComplete &&
    netWorthComplete,
  );

  if (values.sourceOfFundsIds?.includes(otherSoureOfFunds?.value as string)) {
    stepOneCompleted = Boolean(
      stepOneCompleted && values.sourceOfFundsDescription,
    );

    stepOne.sourceOfFundsDescription = values.sourceOfFundsDescription;
  }

  if (stepOneCompleted) {
    stepOneStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepOne).filter((key) => stepOne[key as keyof FormValues])
      .length
  ) {
    stepOneStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepOneStatus;
};

export const getStepTwoStatus = (
  values: Partial<FormValues>,
  stepsStatus?: NewApplicationStepStatus,
  applicationScheme?: ApplicationScheme,
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues,
  accountsCurrenciesValues?: AccountsCurrenciesFormValues[],
  expectedFundsOriginsValues?: ExpectedFundsOriginsFormValues[],
  accountsFlowsValues?: AccountsFlowsFormValues[],
) => {
  let stepTwoStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const monthlyVolumesCompleted = Boolean(
    values.monthlyTurnoverRangeId &&
    values.monthlyNumberIncomingTransactionsRangeId &&
    values.monthlyNumberOutgoingTransactionsRangeId &&
    values.monthlyValueIncomingTransactionsRangeId &&
    values.monthlyValueOutgoingTransactionsRangeId &&
    values.monthlyMaxValueIncomingPaymentRangeId &&
    values.monthlyMaxValueOutgoingPaymentRangeId,
  );

  const productsCompleted = Boolean(
    values.productIds && values.productIds.length,
  );

  let accountsCurrenciesComplete = true;
  let accountsFlowComplete = true;
  let expectedOriginsOfFundsComplete = true;

  if (
    applicationScheme &&
    applicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a'
  ) {
    accountsCurrenciesComplete = false;
    accountsFlowComplete = false;
    expectedOriginsOfFundsComplete = false;

    if (
      accountsCurrenciesValues?.filter(
        (item) =>
          item.currencyId &&
          item.accountOpeningPurpose &&
          item.plannedTransactionNature,
      )?.length
    ) {
      accountsCurrenciesComplete = true;
    }

    if (
      accountsFlowsValues?.filter(
        (item) =>
          item?.expectedAnnualCreditTurnover?.toString() &&
          item?.maxTransferAmountPerTransaction?.toString() &&
          item?.transactionFrequency?.toString() &&
          item?.transactionFrequencyPeriodMonths?.toString(),
      )?.length
    ) {
      accountsFlowComplete = true;
    }

    if (
      expectedFundsOriginsValues?.filter(
        (item) =>
          item?.mainCounterpartiesAndWebsites &&
          item?.countryOfIncorporationId &&
          item?.counterpartyBankCountryIds?.length &&
          item?.activityCountryIds?.length &&
          item?.mainActivities &&
          item?.transactionReason,
      )?.length
    ) {
      expectedOriginsOfFundsComplete = true;
    }
  }

  const stepTwoCompleted = Boolean(
    monthlyVolumesCompleted &&
    productsCompleted &&
    accountsCurrenciesComplete &&
    accountsFlowComplete &&
    expectedOriginsOfFundsComplete,
  );

  const stepTwo: Partial<FormValues> = {
    monthlyTurnoverRangeId: values.monthlyTurnoverRangeId,
    monthlyNumberIncomingTransactionsRangeId:
      values.monthlyNumberIncomingTransactionsRangeId,
    monthlyNumberOutgoingTransactionsRangeId:
      values.monthlyNumberOutgoingTransactionsRangeId,
    monthlyValueIncomingTransactionsRangeId:
      values.monthlyValueIncomingTransactionsRangeId,
    monthlyValueOutgoingTransactionsRangeId:
      values.monthlyValueOutgoingTransactionsRangeId,
    monthlyMaxValueIncomingPaymentRangeId:
      values.monthlyMaxValueIncomingPaymentRangeId,
    monthlyMaxValueOutgoingPaymentRangeId:
      values.monthlyMaxValueOutgoingPaymentRangeId,
  };

  if (values.productIds && values.productIds.length > 0) {
    stepTwo.productIds = values.productIds;
  }

  if (stepTwoCompleted) {
    stepTwoStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepTwo).filter((key) => stepTwo[key as keyof FormValues])
      .length
  ) {
    stepTwoStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepTwoStatus;
};

export const getStepThreeStatus = (
  values: Partial<FormValues>,
  stepsStatus?: NewApplicationStepStatus,
  kycProvider?: string | null,
  kycRequired?: boolean | null,
  applicationScheme?: ApplicationScheme,
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues,
  crsAndFatcaValues?: CRSAndFatcaFormValues[],
  haveSecondPassport?: boolean,
  documents?: ApplicationDocument[],
) => {
  let stepThreeStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  let stepThreeCompleted = false;
  let correspondenceAddressComplete = true;
  let secondPassportComplete = true;
  let pepComplete = true;
  let crsAndFatcaComplete = true;
  let citizenComplete = true;
  let documentsComplete = true;

  if (kycProvider === 'NONE') {
    stepThreeCompleted = true;
  } else if (kycProvider && !kycRequired) {
    stepThreeCompleted = true;
  } else if (kycProvider === null || (kycProvider && kycRequired)) {
    stepThreeCompleted = Boolean(
      values.sumSubStatus === NewApplicationSumSubStatus.GREEN,
    );
  }

  const stepThree: Partial<
    FormValues &
      COFAndIndividualQuestionnarieFormValues & { haveSecondPassport: boolean }
  > = {
    countryId: values.countryId,
    city: values.city,
    postalCode: values.postalCode,
    address1: values.address1,
    address2: values.address2,
    sumSubStatus: values.sumSubStatus,
  };

  if (
    applicationScheme &&
    applicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a'
  ) {
    stepThree.correspondenceAddressSameAsRegisteredAddress =
      values.correspondenceAddressSameAsRegisteredAddress;

    stepThree.haveSecondPassport = haveSecondPassport;

    pepComplete = false;
    crsAndFatcaComplete = false;
    citizenComplete = false;
    documentsComplete = false;

    if (values?.correspondenceAddressSameAsRegisteredAddress) {
      correspondenceAddressComplete = true;
    } else {
      correspondenceAddressComplete = Boolean(
        values.correspondenceAddress1 &&
        values.correspondenceAddress2 &&
        values.correspondenceCity &&
        values.correspondenceCountryId &&
        values.correspondencePostalCode,
      );

      stepThree.correspondenceAddress1 = values.correspondenceAddress1;
      stepThree.correspondenceAddress2 = values.correspondenceAddress2;
      stepThree.correspondenceCity = values.correspondenceCity;
      stepThree.correspondenceCountryId = values.correspondenceCountryId;
      stepThree.correspondencePostalCode = values.correspondencePostalCode;
    }

    if (!haveSecondPassport) {
      secondPassportComplete = true;
    } else {
      secondPassportComplete = Boolean(
        questionnarieFields?.secondPassportExpiryDate &&
        questionnarieFields?.secondPassportIssueCountryId &&
        questionnarieFields?.secondPassportIssueDate &&
        questionnarieFields?.secondPassportNumber,
      );

      stepThree.correspondenceAddress1 =
        questionnarieFields?.secondPassportExpiryDate;
      stepThree.correspondenceAddress2 =
        questionnarieFields?.secondPassportIssueCountryId;
      stepThree.correspondenceCity =
        questionnarieFields?.secondPassportIssueDate;
      stepThree.correspondenceCountryId =
        questionnarieFields?.secondPassportNumber;
    }

    if (
      questionnarieFields?.isPepOrAssociate?.toString() !== 'null' &&
      questionnarieFields?.isPepOrAssociate?.toString() !== 'undefined'
    ) {
      if (questionnarieFields?.isPepOrAssociate === 'no') {
        pepComplete = true;
      } else {
        pepComplete = Boolean(
          questionnarieFields?.nameOfPep &&
          questionnarieFields?.relToPep &&
          questionnarieFields?.orgEmployerAndPosition &&
          questionnarieFields?.positionHeldCountryId &&
          questionnarieFields?.politicalFunctionPeriodFrom &&
          questionnarieFields?.politicalFunctionPeriodTo &&
          questionnarieFields?.abilityMoveGovernmentFunds,
        );

        if (
          questionnarieFields?.abilityMoveGovernmentFunds?.toString() !==
            'null' &&
          questionnarieFields?.abilityMoveGovernmentFunds?.toString() !==
            'undefined' &&
          questionnarieFields?.abilityMoveGovernmentFunds === 'yes'
        ) {
          if (!questionnarieFields?.abilityMoveGovernmentFundsDetails) {
            pepComplete = false;
          } else {
            pepComplete = Boolean(
              pepComplete &&
              questionnarieFields?.abilityMoveGovernmentFundsDetails,
            );
          }
        }
      }
    }

    if (
      crsAndFatcaValues?.filter(
        (item) =>
          item?.residenceCountryId &&
          (item?.taxIdentificationNumber || item?.noTinReason),
      )?.length
    ) {
      crsAndFatcaComplete = true;
    }

    if (values.isUSResident && questionnarieFields?.isUSCitizen) {
      citizenComplete = true;
    }

    if (
      documents?.filter(
        (item) => !item.documentCategoryId?.includes('identification'),
      )?.length
    ) {
      if (
        !documents
          ?.filter(
            (item) => !item.documentCategoryId?.includes('identification'),
          )
          ?.filter((item) => item.status === 'NEW').length
      ) {
        documentsComplete = true;
      }
    } else if (
      !documents?.filter(
        (item) => !item.documentCategoryId?.includes('identification'),
      )?.length
    ) {
      documentsComplete = true;
    }
  }

  const stepGeneralThreeComplete = Boolean(
    stepThreeCompleted &&
    correspondenceAddressComplete &&
    secondPassportComplete &&
    pepComplete &&
    crsAndFatcaComplete &&
    citizenComplete &&
    documentsComplete,
  );

  if (stepGeneralThreeComplete) {
    stepThreeStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepThree).filter((key) => stepThree[key as keyof FormValues])
      .length
  ) {
    stepThreeStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepThreeStatus;
};

export const getStepFourStatus = (
  agreements?: ApplicationAgreement[],
  stepsStatus?: NewApplicationStepStatus,
  agreementsActive?: boolean | null,
  agreementsRequired?: boolean | null,
) => {
  let stepFourStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const unsignedAgreements: ApplicationAgreement[] = [];

  agreements?.forEach((item) => {
    if (!item.signatures.edges.length) {
      unsignedAgreements.push(item);
    }
  });

  let agreementsCompleted = false;

  if (agreementsActive && !agreementsRequired) {
    agreementsCompleted = true;
  } else if (
    ((agreementsActive === null || agreementsActive === undefined) &&
      !unsignedAgreements.length &&
      agreements) ||
    (agreementsRequired && !unsignedAgreements.length && agreements)
  ) {
    agreementsCompleted = true;
  } else if (
    agreementsActive !== null &&
    agreementsActive !== undefined &&
    !agreementsActive
  ) {
    agreementsCompleted = true;
  }

  if (agreementsCompleted) {
    stepFourStatus = NewApplicationStepStatus.COMPLETED;
  }

  return stepFourStatus;
};

export const getStepFiveStatus = (
  ongoingMonitoring?: OngoingMonitring[],
  ongoingCheckingStatus?: OngoingCheckingStatus,
  stepsStatus?: NewApplicationStepStatus,
) => {
  let stepFiveStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const ongoingWithWarnings: OngoingMonitring[] = [];

  if (ongoingMonitoring && ongoingMonitoring?.length > 0) {
    stepFiveStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  ongoingMonitoring?.forEach((item) => {
    item?.warnings?.edges?.forEach((warning) => {
      if (!warning?.node?.action || warning?.node?.action === 'STOP') {
        ongoingWithWarnings.push(item);
      }
    });
  });

  if (ongoingMonitoring && !ongoingWithWarnings.length) {
    stepFiveStatus = NewApplicationStepStatus.COMPLETED;
  } else if (ongoingCheckingStatus?.status === OngoingStatus.FAIL) {
    stepFiveStatus = NewApplicationStepStatus.COMPLETED;
  }

  return stepFiveStatus;
};

export const updateApplicationStepStatuses = ({
  values,
  stepsStatus,
  sourcesOfFunds,
  clientsActivities,
  agreements,
  ongoingMonitoring,
  ongoingCheckingStatus,
  applicationScheme,
  questionnarieFields,
  accountsCurrenciesValues,
  expectedFundsOriginsValues,
  accountsFlowsValues,
  crsAndFatcaValues,
  haveSecondPassport,
  documents,
}: {
  values: FormValues;
  stepsStatus?: NewApplicationIndividualStepsStatuses;
  sourcesOfFunds?: SelectItemProps[];
  clientsActivities?: SelectItemProps[];
  agreements?: ApplicationAgreement[];
  ongoingMonitoring?: OngoingMonitring[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
  applicationScheme?: ApplicationScheme;
  questionnarieFields?: COFAndIndividualQuestionnarieFormValues;
  accountsCurrenciesValues?: AccountsCurrenciesFormValues[];
  expectedFundsOriginsValues?: ExpectedFundsOriginsFormValues[];
  accountsFlowsValues?: AccountsFlowsFormValues[];
  crsAndFatcaValues?: CRSAndFatcaFormValues[];
  haveSecondPassport?: boolean;
  documents?: ApplicationDocument[];
}): NewApplicationIndividualStepsStatuses => {
  let agreementsActive = null;
  let agreementsRequired = null;

  if (
    applicationScheme &&
    applicationScheme?.agreementsActive?.toString() !== 'null'
  ) {
    agreementsActive = applicationScheme?.agreementsActive;
  }

  if (
    applicationScheme &&
    applicationScheme?.agreementsRequired?.toString() !== 'null'
  ) {
    agreementsRequired = applicationScheme?.agreementsRequired;
  }

  const nextStepsStatus: NewApplicationIndividualStepsStatuses = {
    stepOne: getStepOneStatus(
      values,
      stepsStatus?.stepOne,
      sourcesOfFunds,
      clientsActivities,
      applicationScheme?.taxNumberRequired,
      applicationScheme,
      questionnarieFields,
    ),
    stepTwo: getStepTwoStatus(
      values,
      stepsStatus?.stepTwo,
      applicationScheme,
      questionnarieFields,
      accountsCurrenciesValues,
      expectedFundsOriginsValues,
      accountsFlowsValues,
    ),
    stepThree: getStepThreeStatus(
      values,
      stepsStatus?.stepThree,
      applicationScheme?.kycProvider || null,
      applicationScheme?.kycRequired || false,
      applicationScheme,
      questionnarieFields,
      crsAndFatcaValues,
      haveSecondPassport,
      documents,
    ),
    stepFour: getStepFourStatus(
      agreements,
      stepsStatus?.stepFour,
      agreementsActive,
      agreementsRequired,
    ),
    stepFive: getStepFiveStatus(
      ongoingMonitoring,
      ongoingCheckingStatus,
      stepsStatus?.stepFive,
    ),
  };

  return nextStepsStatus;
};
